import React, { useEffect, useMemo, useState } from 'react';
import { IonAlert, IonContent, IonPage } from '@ionic/react';
import { Link, useHistory } from 'react-router-dom';
import HelpDeskEntry from './HelpDeskEntry';
import { ApiService } from '../api/ApiService';
import { PlatformUtils } from '../utils/PlatformUtils';
import { FaqEntry } from '../api/model/FaqEntry';
import LoadDetailHeader, { useTransparentHeaderContrast } from '../components/LoadDetailHeader';
import { ChevronRight, HelpDeskIcon } from '../components/icons';
import { ChainOfContactSvg, MoneySvg } from '../components/svgIcons';

function HelpDeskFaqItem({
  question,
  children,
  index,
}: {
  question: string;
  children: React.ReactNode;
  index: number;
}) {
  const [open, setOpen] = useState(false);

  return (
    <div
      className={`payroll-faq-item${open ? ' is-open' : ''}`}
      style={{ ['--payroll-faq-delay' as string]: `${0.3 + index * 0.04}s` }}
    >
      <button
        type="button"
        className="payroll-faq-item__header"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
      >
        <span className="payroll-faq-item__question">{question}</span>
        <span className={`payroll-faq-item__chevron${open ? ' is-open' : ''}`} aria-hidden="true">
          <ChevronRight color="currentColor" />
        </span>
      </button>
      {open ? <div className="payroll-faq-item__answer">{children}</div> : null}
    </div>
  );
}

const HelpDesk: React.FC = () => {
  const history = useHistory();
  const { onSheet, onIonScroll } = useTransparentHeaderContrast();
  const [mounted, setMounted] = useState(false);

  const [showAlert, setShowAlert] = useState(false);
  const [storeLink, setStoreLink] = useState('');
  const [faqEntries, setFaqEntries] = useState<FaqEntry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const id = requestAnimationFrame(() => {
      setTimeout(() => setMounted(true), 30);
    });
    return () => cancelAnimationFrame(id);
  }, []);

  useEffect(() => {
    ApiService.sendEventCommand('Helpdesk View', 'eventCheck', 'User viewed Helpdesk entries');
    ApiService.getFaqEntries('helpDesk', { isLp: 1 })
      .then(it => setFaqEntries(Array.isArray(it) ? it : []))
      .finally(() => setLoading(false));

    const link = PlatformUtils.getStoreLink();
    if (link) setStoreLink(link);
  }, []);

  const activeFaqs = useMemo(() => faqEntries.filter(entry => entry.active !== 0), [faqEntries]);

  const goBack = () => {
    if (history.length > 1) history.goBack();
    else history.replace('/Home');
  };

  const faqCount = activeFaqs.length + 3;

  return (
    <IonPage>
      <LoadDetailHeader title="Help Desk" onBack={goBack} onSheet={onSheet} />

      <IonAlert
        isOpen={showAlert}
        onDidDismiss={() => setShowAlert(false)}
        header={"You don't currently have the Transflo+ app installed. Would you like to download it from the app store?"}
        message={'Reminder: when registering with Transflo+, please use WSXI as the Recipient ID.'}
        buttons={[
          { text: 'Cancel', role: 'cancel', cssClass: 'secondary' },
          { text: 'Download', handler: () => { window.open(storeLink); } },
        ]}
      />

      <IonContent
        fullscreen
        className="design-a-content design-a-content--light"
        scrollEvents={true}
        onIonScroll={onIonScroll}
      >
        <div className={`design-a design-a--detail${mounted ? ' design-a--mounted' : ''}`}>
          <div className="home-hero advance-hero">
            <div className="safety-hero__badge" aria-hidden="true">
              <HelpDeskIcon color="#ffffff" size={26} />
            </div>
            <div className="advance-hero__eyebrow">Support</div>
            <div className="advance-hero__title home-display">Frequently Asked Questions</div>
            <div className="advance-hero__meta">
              {loading ? 'Loading answers…' : `${faqCount} frequently asked questions`}
            </div>
          </div>

          <div className="home-sheet help-desk-sheet">
            <section className="safety-section">
              <h2 className="safety-section__title home-section-label">Common questions</h2>
              <div className="payroll-faq-list">
                <HelpDeskFaqItem index={0} question="Who do I reach out to if I have a payroll issue?">
                  <p>Speak with a member of our accounting team.</p>
                  <Link to="/keycontacts" className="help-desk-action">
                    <span className="help-desk-action__icon" aria-hidden="true">
                      <MoneySvg color="currentColor" />
                    </span>
                    <span className="help-desk-action__label">Go to Key Contacts</span>
                    <ChevronRight color="currentColor" />
                  </Link>
                </HelpDeskFaqItem>

                <HelpDeskFaqItem index={1} question="Can I be a trainer as a LP driver?">
                  <p>
                    Absolutely. Training may be a great way for you to increase mileage and revenue in
                    your business. Contact your agent for more information.
                  </p>
                </HelpDeskFaqItem>

                <HelpDeskFaqItem
                  index={2}
                  question="Who do I contact if I have an issue while my agent is out?"
                >
                  <p>
                    There is someone here to assist with your business, 24/7. If it's late at night or on
                    the weekend, send in a tablet message or give us a call and someone will assist you
                    until your agent returns to work.
                  </p>
                  <Link to="/chainofcommand" className="help-desk-action">
                    <span className="help-desk-action__icon" aria-hidden="true">
                      <ChainOfContactSvg color="currentColor" />
                    </span>
                    <span className="help-desk-action__label">Go to Chain of Contact</span>
                    <ChevronRight color="currentColor" />
                  </Link>
                </HelpDeskFaqItem>
              </div>
            </section>

            {activeFaqs.length > 0 ? (
              <section className="safety-section">
                <h2 className="safety-section__title home-section-label">More questions</h2>
                <div className="payroll-faq-list">
                  {activeFaqs.map((entry, index) => (
                    <HelpDeskEntry key={entry.id} element={entry} index={index + 3} />
                  ))}
                </div>
              </section>
            ) : null}
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default HelpDesk;
